// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import {
  getEventoInfo,
  getActiveAmbassadorsForEvent,
} from "@/features/evento/eventoThunks";
import {
  validateReferralCode,
  trackReferralClick,
} from "@/features/embajador/embajadorThunks";
import { getMiSolicitudEnEvento } from "@/features/invitacion/invitacionThunks";

// React Utils
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  useParams,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";

// Cart context
import { useCart } from "@/contexts/CartContext";

// Toast
import { useToast, ToastContainer } from "@/components/ui/Toast";

// Referral utilities
import {
  saveReferralForEvent,
  getReferralForEvent,
  cleanExpiredReferrals,
} from "@/utils/referralLinkManager";

// Componentes
import Breadcrum from "@/components/Breadcrum";
import CardEvento from "@/components/DetailSaleEvento/CardEvento";
import CardEmbajadores from "@/components/DetailSaleEvento/CardEmbajadores";
import CardTickets from "@/components/DetailSaleEvento/CardTickets";
import FixedAddToCart from "@/components/DetailSaleEvento/FixedAddToCart";
import CartModal from "@/components/Modals/CartModal";
import PaymentModal from "@/components/Modals/PaymentCartModal";
import PaySuccessModal from "@/components/Modals/PaymentCartModal/PaySuccessModal";
import PayFailModal from "@/components/Modals/PaymentCartModal/PayFailModal";
import AmbassadorReferralBanner from "@/components/AmbassadorReferralBanner";
import GenerateReferralLink from "@/components/GenerateReferralLink";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Componentes segunda sección
import CardAseguraTuTicket from "@/components/DetailSaleEvento/CardAseguraTuTicket";
import CardProximasFechas from "@/components/DetailSaleEvento/CardProximasFechas";

// Assets
import EventImage1 from "@/assets/eventos/ViejasDeMierda.webp";
import Ambassador5 from "@/assets/recentAmbassador/mujer5.webp";
import Ambassador6 from "@/assets/recentAmbassador/duo.webp";
import Ambassador7 from "@/assets/recentAmbassador/chris.webp";
import LogoPerfilEmpresa from "@/assets/avatars/avatar_amanda.webp";

// Simulacion de props para el componente
// Lista de próximas fechas (eventos)

//Utils
import { formatFechaHoraEvento } from "@/lib/utilsUI";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import { scrollToTop } from "@/lib/utilsUI";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

export default function EventoDetail() {
  // Obtener la ubicación actual
  const location = useLocation();
  // Obtener el ID del evento desde los parámetros de la URL
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // console.log("EventoDetail mounted with id:", id, "and location:", location?.search.includes("ref="), "searchParams:", Array.from(searchParams.entries()));

  // ========== REFS PARA EVITAR PETICIONES DUPLICADAS ==========
  const hasValidatedReferral = useRef(false);
  const hasLoadedEventData = useRef(false);

  // Estados locales para el manejo de la carga
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoadComplete, setDataLoadComplete] = useState(false);
  const [reRender, setReRender] = useState(location.pathname);

  // Referral states
  const [referralInfo, setReferralInfo] = useState(null);
  const [showReferralBanner, setShowReferralBanner] = useState(false);

  // Modal
  const [isOpenPayment, setIsOpenPayment] = useState(false);
  const [selectedCarritoUid, setSelectedCarritoUid] = useState(null);
  const [selectedKlapOrderId, setSelectedKlapOrderId] = useState(null); // Estado para el klapOrderId
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isFailOpen, setIsFailOpen] = useState(false);

  // Redux state y dispatch
  const dispatch = useDispatch();
  const { infoEvento, loading, error, activeAmbassadors } = useSelector(
    (state) => state.evento,
  );
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  // Cart context
  const { carts, isCartModalOpen, openCartModal, closeCartModal } = useCart();

  // Toast
  const { toasts, removeToast, error: showError } = useToast();

  // Tickets state for cart integration
  const [ticketsInfo, setTicketsInfo] = useState({
    hasTickets: false,
    totalTickets: 0,
    addToCart: null,
  });
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    setReRender(location.pathname);
  }, [location.pathname]);

  // Detectar y validar referral code en la URL
  useEffect(() => {
    // Prevenir ejecución duplicada
    if (hasValidatedReferral.current) return;
    const referralCode = searchParams.get("ref");

    if (referralCode && id) {
      hasValidatedReferral.current = true;
      // Limpiar referrals expirados
      cleanExpiredReferrals();

      // Validar el código con el backend usando el thunk
      const validateReferral = async () => {
        try {
          const result = await dispatch(
            validateReferralCode({ referralCode, eventoUid: id }),
          ).unwrap();

          if (result.valid) {
            const { ambassadorInfo } = result;

            // Guardar en LocalStorage
            saveReferralForEvent(id, referralCode, {
              nombre: ambassadorInfo.nombre,
              apellido: ambassadorInfo.apellido,
              alias: ambassadorInfo.alias,
            });

            // Mostrar banner
            setReferralInfo(ambassadorInfo);
            setShowReferralBanner(true);

            // Registrar click (solo si hay token)
            // console.log("Token for tracking referral click", Object.keys(localStorage));
            let token = localStorage.getItem("persist:auth");

            if (token) {
              const parsedToken = JSON.parse(token);
              token = parsedToken?.idToken;

              if (token !== null && token !== "null") {
                try {
                  await dispatch(
                    trackReferralClick({ referralCode, eventoUid: id }),
                  ).unwrap();
                } catch (trackError) {
                  console.error("Error tracking referral click:", trackError);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error validating referral code:", error);
        }
      };

      validateReferral();
    } else if (!hasValidatedReferral.current) {
      // Si no hay código en la URL, verificar si hay uno guardado para este evento
      hasValidatedReferral.current = true;
      const savedReferral = getReferralForEvent(id);
      if (savedReferral) {
        setReferralInfo({
          nombre: savedReferral.ambassadorName,
          apellido: savedReferral.ambassadorLastName,
          alias: savedReferral.ambassadorAlias,
        });
        setShowReferralBanner(true);
      }
    }
  }, [searchParams, id, dispatch]);

  // Cargar evento al montar el componente
  useEffect(() => {
    // Prevenir ejecución duplicada
    if (hasLoadedEventData.current) return;
    const loadData = async () => {
      if (id) {
        hasLoadedEventData.current = true;
        setIsLoading(true);

        try {
          await dispatch(getEventoInfo({ uidEvento: id }));
          setDataLoadComplete(true);
        } catch (error) {
          console.error("Error loading event data:", error);
          setDataLoadComplete(true);
        }
      }
    };

    loadData();
  }, [dispatch, id]);

  // Cargar embajadores activos cuando el evento tiene useAmbassador == 2
  useEffect(() => {
    const loadActiveAmbassadors = async () => {
      if (infoEvento && infoEvento.useAmbassador === 2 && id) {
        try {
          await dispatch(getActiveAmbassadorsForEvent({ uidEvento: id }));
        } catch (error) {
          console.error("Error loading active ambassadors:", error);
        }
      }
    };

    loadActiveAmbassadors();
  }, [dispatch, id, infoEvento?.useAmbassador]);

  // Cargar solicitud de invitacion del usuario para este evento (solo si hay tickets de invitacion)
  useEffect(() => {
    const tieneInvitacion = infoEvento?.tiposTicket?.some((t) => t.esInvitacion);
    if (isAuthenticated && user?.uid && infoEvento?.uid && tieneInvitacion) {
      dispatch(getMiSolicitudEnEvento({ uidEvento: infoEvento.uid }));
    }
  }, [dispatch, infoEvento?.uid, isAuthenticated, user?.uid]);

  // Verificar si todas las cargas de Redux han terminado
  useEffect(() => {
    if (dataLoadComplete && !loading) {
      // Pequeño delay para asegurar que la pantalla de carga se vea
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [dataLoadComplete, loading]);

  // Si hay errores, mostrar la pantalla de carga por un tiempo mínimo
  useEffect(() => {
    if (error) {
      const errorTimer = setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      return () => clearTimeout(errorTimer);
    }
  }, [error]);

  const isLoadingVerify = isLoading || loading || !dataLoadComplete;

  // Verificar si el usuario autenticado es un embajador activo para este evento
  const isActiveAmbassadorForEvent = useCallback(() => {
    if (!isAuthenticated || !user?.embajadorId || !activeAmbassadors) {
      return false;
    }

    return activeAmbassadors.some(
      (ambassador) => ambassador.embajadorId === user.embajadorId,
    );
  }, [isAuthenticated, user?.embajadorId, activeAmbassadors]);

  // Stable callback for tickets changes using useCallback
  const handleTicketsChange = useCallback((info) => {
    setTicketsInfo(info);
  }, []);

  // Transform backend data to component format
  const evento = infoEvento
    ? {
        id: infoEvento.uid,
        titulo: infoEvento.nombre,
        fecha: formatFechaHoraEvento(
          parseUTCDate(
            infoEvento?.fechaHoraInicio,
            infoEvento?.zonaHoraria || DEFAULT_TIMEZONE,
          )?.toJSDate(),
          infoEvento?.zonaHoraria || DEFAULT_TIMEZONE,
        ),
        fechaHoraFin: infoEvento?.fechaHoraFin,
        subtitulo: infoEvento.enPromocion ? infoEvento.textoPromocion : "",
        img: infoEvento.imgEvento || null,
        enDescuento: !!infoEvento.enPromocion,
        descripcion: infoEvento.descripcion || "",
        ubicacion: infoEvento.ubicacion || "",
        direccion: infoEvento.direccion || "",
        tickets: infoEvento.tiposTicket || [],
        useAmbassador: infoEvento.useAmbassador || 0,
        palabrasClave: [], // This can be added later if needed
        embajadores: infoEvento.embajadores || [],
        totalEmbajadores: infoEvento.totalEmbajadores || 0,
        // Add stable callback for tickets changes
        onTicketsChange: handleTicketsChange,
      }
    : null;

  // Lista de próximos eventos
  const proximosEventos = [
    {
      img: EventImage1,
      titulo: "Alpine Escape",
      fecha: "31/10/2025",
      rating: 4,
      ratingText: "4/5",
    },
    {
      img: EventImage1,
      titulo: "Caribbean Cruise",
      fecha: "31/10/2025",
      rating: 5,
      ratingText: "5/5",
    },
    {
      img: EventImage1,
      titulo: "Parisian Romance",
      fecha: "31/10/2025",
      rating: 4.5,
      ratingText: "4.5/5",
    },
    {
      img: EventImage1,
      titulo: "Greek Island Hopping",
      fecha: "31/10/2025",
      rating: 4.5,
      ratingText: "4.5/5",
    },
  ];

  // Lista de beneficios
  const benefitsList = [
    {
      icon: "certificate",
      title: "Empresa Verificada",
      description:
        "Enjoy gourmet meals, drinks, and activities included in your package",
    },
    {
      icon: "fan",
      title: "Ticket de Cortesía",
      description:
        "Contamos con invitaciones de cortesía. Te debes validar desde el link",
    },
    {
      icon: "water",
      title: "Water Sports",
      description: "Access to snorkeling, diving, and other water sports",
    },
    {
      icon: "person-rays",
      title: "Luxury Accommodation",
      description:
        "Stay in a private overwater villa with breathtaking ocean views",
    },
    {
      icon: "seedling",
      title: "Sustainability",
      description:
        "Eco-friendly resort with a commitment to preserving marine life and supporting local communities.",
    },
  ];

  const breadcrumLinks = [
    { label: "Home", href: "/" },
    { label: `${evento?.titulo || ""}`, href: "#" },
  ];
  const currentDateTime = new Date();
  // Layout principal
  return (
    <>
      {/* Barra de CTA para agregar los tickets seleccionados */}
      {isLoadingVerify ? (
        <SkeletonTheme
          baseColor="#0f172a"
          highlightColor="#1e293b"
          className="sm:hidden top-0"
        >
          <div className="w-full sticky bottom-0 z-[80]">
            <Skeleton height={70} className="w-full" />
          </div>
        </SkeletonTheme>
      ) : evento ? (
        <>
          {reRender && (
            <FixedAddToCart
              evento={evento}
              responsive={"sm:hidden top-0"}
              onAddToCart={ticketsInfo.addToCart}
              ticketsInfo={ticketsInfo}
            />
          )}
        </>
      ) : null}

      <div className="relative min-h-screen bg-aw-bluedark-95 sm:bg-transparent w-full flex flex-col px-4 pb-6 pt-2">
        {/* Navegación */}
        <Breadcrum links={breadcrumLinks} />

        {/* Banner de referido */}
        {showReferralBanner &&
          referralInfo &&
          location?.search.includes("ref=") && (
            <div className="">
              <AmbassadorReferralBanner
                ambassadorName={referralInfo.nombre}
                ambassadorLastName={referralInfo.apellido}
                ambassadorAlias={referralInfo.alias}
                onDismiss={() => setShowReferralBanner(false)}
              />
            </div>
          )}

        {/* Sección de Embajador: Generar Link de Referido */}
        {isAuthenticated &&
          user?.perEmbajador == 1 &&
          evento?.fechaHoraFin &&
          currentDateTime <
            parseUTCDate(
              evento?.fechaHoraFin,
              evento?.zonaHoraria || DEFAULT_TIMEZONE,
            )?.toJSDate() &&
          (evento?.useAmbassador == 1 ||
            (evento?.useAmbassador == 2 && isActiveAmbassadorForEvent())) && (
            <div className="mb-2">
              <GenerateReferralLink
                eventoUid={evento.id}
                eventoNombre={evento.titulo}
              />
            </div>
          )}

        {/* Contenido principal */}
        {isLoadingVerify ? (
          <SkeletonTheme baseColor="#0f172a" highlightColor="#1e293b">
            <div className="flex flex-col lg:flex-row gap-y-5 sm:gap-10 mt-2">
              {/* Card del evento */}
              <div className="flex-1 w-full flex flex-col items-center lg:items-start">
                <Skeleton width={400} height={500} borderRadius={8} />
              </div>

              {/* Card móvil de tickets */}
              <div className="sm:hidden flex-1 w-full flex flex-col items-center lg:items-end">
                <Skeleton height={500} borderRadius={8} />
              </div>

              {/* Card central de embajadores */}
              <div className="flex-1 w-full flex flex-col items-center">
                <Skeleton width={400} height={500} borderRadius={8} />
              </div>

              {/* Card desktop de tickets */}
              <div className="hidden flex-1 w-full sm:flex flex-col items-center lg:items-end">
                <Skeleton width={400} height={500} borderRadius={8} />
              </div>
            </div>

            <hr className="my-6 border-aw-green/25" />

            {/* Fixed bottom CTA skeleton */}
            <div className="hidden sm:block">
              <Skeleton height={70} className="w-full" />
            </div>
          </SkeletonTheme>
        ) : error ? (
          <div className="flex justify-center items-center min-h-[60vh] bg-aw-bluedark-95">
            <div className="text-center p-8">
              <p className="text-red-400 text-xl mb-2">
                Error al cargar el evento
              </p>
              <p className="text-gray-400">
                No se encontró la información de este evento.
              </p>
              <p className="text-gray-400">
                Comunícate con AppWhoop para dar solución.
              </p>
            </div>
          </div>
        ) : !evento ? (
          <div className="flex justify-center items-center min-h-[60vh] bg-aw-bluedark-95">
            <div className="text-center p-8">
              <p className="text-gray-400 text-xl mb-2">Evento no encontrado</p>
              <p className="text-gray-400">
                No se encontró la información de este evento.
              </p>
              <p className="text-gray-400">
                Comunícate con AppWhoop para dar solución.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Sección superior: Card del evento, Card de embajadores y Card de tickets */}
            <div className="flex flex-col lg:flex-row gap-y-5 sm:gap-10 mt-2">
              {/* Card que muestra el evento */}
              <div className="flex-1 w-full sm:w-1/3 flex flex-col items-center lg:items-start">
                <CardEvento evento={evento} />
              </div>
              {/* Card que muestra en móviles las funciones de agregar o disminuir cantidad de tickets y postular a ticket gratuito */}
              <div
                className="sm:hidden flex-1 w-full sm:w-1/3 flex flex-col items-center lg:items-end"
                data-tickets-section
              >
                <CardTickets
                  evento={evento}
                  LogoPerfilEmpresa={LogoPerfilEmpresa}
                />
              </div>
              <div className="flex-1 w-full sm:w-1/3 flex flex-col items-center">
                {/* Card central donde se muestra el staff y la información relevante del evento */}
                <CardEmbajadores evento={evento} />
              </div>
              {/* Card que muestra en noMóviles las funciones de agregar o disminuir cantidad de tickets y postular a ticket gratuito */}
              <div
                className="hidden flex-1 w-full sm:w-1/3 sm:flex flex-col items-center lg:items-end"
                data-tickets-section
              >
                <CardTickets
                  evento={evento}
                  LogoPerfilEmpresa={LogoPerfilEmpresa}
                />
              </div>
            </div>

            <hr className="my-4 border-aw-green" />

            {/* Sección de texto sobre el evento */}
            <div className="bg-aw-bluedark text-xs text-aw-bluegray rounded-2xl mb-4 p-2">
              <p>
                Todo el contenido de este evento, incluyendo valores, horarios,
                ubicación, descuentos y beneficios, es responsabilidad exclusiva
                de <Link
                onClick={scrollToTop}
                  to={`/empresa/${infoEvento.empresaUid}`}
                  className="text-aw-green/50"
                >
                  {infoEvento.empresaNombre}
                </Link>. AppWhoop actúa únicamente
                como plataforma de difusión y no interviene ni asume
                responsabilidad sobre ninguno de estos aspectos. <Link
                onClick={scrollToTop}
                  to={`/empresa/${infoEvento.empresaUid}`}
                  className="text-aw-green/50"
                >
                  {infoEvento.empresaNombre}
                </Link> es
                la única y exclusiva responsable de la producción, organización
                y cumplimiento legal del evento.
              </p>
              <br />
              <p>
                AppWhoop facilita la venta de tickets en línea como
                intermediario, limitando su responsabilidad estrictamente al
                procesamiento de transacciones. Cualquier aspecto relacionado
                con la calidad del evento, descuentos o beneficios ofrecidos de
                este evento recaen íntegramente en{" "}
                <Link
                onClick={scrollToTop}
                  to={`/empresa/${infoEvento.empresaUid}`}
                  className="text-aw-green/50"
                >
                  {infoEvento.empresaNombre}
                </Link>, quien debe garantizar su cumplimiento de forma
                oportuna y conforme a la normativa vigente. Al utilizar este
                sitio, usted acepta nuestros{" "}
                <Link
                onClick={scrollToTop} 
                to="/politica-de-privacidad-y-terminos-y-condiciones" className="text-aw-green/50">
                  Términos y Condiciones
                </Link>
                .
              </p>
            </div>

            {/* Barra de CTA para agregar los tickets seleccionados */}

            {reRender && (
              <FixedAddToCart
                evento={evento}
                responsive={"hidden sm:block bottom-0"}
                onAddToCart={ticketsInfo.addToCart}
                ticketsInfo={ticketsInfo}
              />
            )}
          </>
        )}
      </div>

      {/* Cart Modal */}
      {evento && (
        <CartModal
          isOpen={isCartModalOpen}
          onClose={closeCartModal}
          carts={carts}
          showError={showError}
          onOpenPayment={(carritoUid, klapOrderId) => {
            // Recibe carritoUid y klapOrderId desde CartSummary
            setSelectedCarritoUid(carritoUid);
            setSelectedKlapOrderId(klapOrderId);
            // setIsOpenPayment(true);
          }}
        />
      )}
      {/* <PaymentModal
        isOpen={isOpenPayment}
        onClose={() => setIsOpenPayment(false)}
        onBack={openCartModal}
        openSuccessModal={() => {
          // close cart modal
          closeCartModal();
          // Close payment modal and open success modal
          setIsOpenPayment(false);
          // Open success modal
          setIsSuccessOpen(true)
        }}
        openFailModal={() => {
          // close cart modal
          closeCartModal();
          // Close payment modal and open fail modal
          setIsOpenPayment(false);
          // Open fail modal
          setIsFailOpen(true)
        }}
        carritoUid={selectedCarritoUid}
        klapOrderId={selectedKlapOrderId}
      />
      <PaySuccessModal
        isOpen={isSuccessOpen}
        onClose={() => setIsSuccessOpen(false)}
      />
      <PayFailModal isOpen={isFailOpen} onClose={() => setIsFailOpen(false)} /> */}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </>
  );
}
